import React, { useContext } from 'react';
import { z } from 'zod';

import { RouteContext } from 'src/context';

// Helper function to safely parse and validate data
function safeParsePrefillData<T extends z.ZodTypeAny>(
  schema: T,
  data: unknown,
): z.infer<T> | null {
  const result = schema.safeParse(data);
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return result.success ? result.data : null;
}

/**
 * Generic hook for handling prefilled data from query parameters.
 * Looks for a 'pf' query param, decodes it from base64, and validates it against a Zod schema.
 *
 * @param schema - Zod schema to validate the prefilled data against
 * @returns Object containing the action (pageAction) and validated prefilled data
 */
export function usePrefill<T extends z.ZodTypeAny>(schema: T) {
  const { query } = useContext(RouteContext);
  const { action, pf } = query;

  const prefilledData = React.useMemo((): z.infer<T> | null => {
    if (!pf) {
      return null;
    }

    try {
      // Decode base64 string
      const decodedString = atob(pf);

      // Parse JSON
      const parsedData = JSON.parse(decodedString);

      // Validate against schema using safeParse
      const validatedData = safeParsePrefillData(schema, parsedData);

      if (validatedData) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return
        return validatedData;
      } else {
        console.error('Failed to validate prefilled data');
        return null;
      }
    } catch (error) {
      console.error('Failed to parse prefilled data:', error);
      return null;
    }
  }, [pf, schema]);

  return {
    action,
    prefilledData,
  };
}
